import React, { useEffect, useState } from "react";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { usePrivacyCenterSettings } from "../../../components/superorg/settings/PrivacyCenterSettings";
import { GetCompliantFooter } from "../ProgressFooter";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { CodeSnippet } from "../../../components/organisms/CodeSnippet";
import { getPrivacyCenterCnameValue } from "../../../util/privacyCenterUtil";
import { Text } from "../../../../common/components/input/Text";
import { Adornment } from "../../../components/input/Adornment";
import { Form } from "react-final-form";
import { Api } from "../../../../common/service/Api";
import { Callout, CalloutVariant } from "../../../components/Callout";
import { CheckCircle, Info } from "@mui/icons-material";
import { Loading } from "../../../components";
import { LoadingButton } from "../../../../common/components/LoadingButton";

export const VerifySubdomainPage: React.FC = () => {
  const {
    settings: pcSettings,
    orgRequest: pcOrgRequest,
    privacyCentersRequest: pcRequest,
    privacyCenter,
  } = usePrivacyCenterSettings();

  const [subdomainError, setSubdomainError] = useState<boolean | string>("");
  const [subdomainSuccessful, setSubdomainSuccessful] = useState<boolean>(false);
  const [subdomainLoading, setSubdomainLoading] = useState<boolean>(false);

  const verifyPrivacyCenterDomain = async (domain: string): Promise<void> => {
    setSubdomainLoading(true);
    setSubdomainSuccessful(false);
    setSubdomainError("");
    const res = await Api.privacyCenter.verifyPrivacyCenterDomain(
      privacyCenter.organizationId,
      privacyCenter.id,
      domain,
    );
    setSubdomainLoading(false);
    if (res) {
      setPrivacyCenterDomain(domain);
      setSubdomainSuccessful(true);
    } else {
      setSubdomainError(res === false ? domain : true);
    }
  };

  const [privacyCenterDomain, setPrivacyCenterDomain] = useState<string>("");
  useEffect(() => {
    if (privacyCenter?.domains?.length) {
      setPrivacyCenterDomain(privacyCenter.domains[0].customDomain);
    }
  }, [privacyCenter]);

  let nextDisabled = false;
  let nextDisabledTooltip = "";
  if (!privacyCenterDomain) {
    nextDisabled = true;
    nextDisabledTooltip = "You must verify your privacy center subdomain before proceeding.";
  }

  if (!pcSettings) {
    return <Loading />;
  }

  return (
    <GetCompliantPageWrapper
      progressFooter={
        <GetCompliantFooter
          presentStep="DataRetention.VerifySubdomain"
          nextDisabled={nextDisabled}
          nextDisabledTooltip={nextDisabledTooltip}
        />
      }
      requests={[pcOrgRequest, pcRequest]}
    >
      <PageHeader titleContent="Verify Subdomain" />

      <div>
        <div className="text-body-1-medium m-0">Verify your new subdomain</div>
        <p>
          Let’s recheck that your CNAME is set up. Click below to verify that your subdomain is
          working.
        </p>
        {!privacyCenter?.allowWebIndexing && (
          <p>
            Note: We’ve added a special tag on our privacy centers called{" "}
            <code>&lt;meta name="robots" content="noindex, nofollow"&gt;</code>. This tag ensures
            that Google and other search engines won't include your policy in their search results.
          </p>
        )}
        <Form
          onSubmit={(values) => {
            verifyPrivacyCenterDomain(values.domain);
          }}
          render={({ handleSubmit, values }) => {
            return (
              <form onSubmit={handleSubmit}>
                <div className="flex-row">
                  <Text
                    multiline={false}
                    variant="outlined"
                    field="domain"
                    className="mr-md my-0"
                    fullWidth
                    size="small"
                    initialValue={privacyCenterDomain}
                    disabled={Boolean(privacyCenterDomain)}
                    InputProps={{
                      startAdornment: <Adornment position="start">https://</Adornment>,
                    }}
                    required
                  />
                  <LoadingButton
                    disabled={Boolean(privacyCenterDomain) || !values.domain}
                    loading={subdomainLoading}
                    type="submit"
                    variant="contained"
                    className="min-width-160"
                  >
                    Verify Subdomain
                  </LoadingButton>
                </div>
                <div className="mt-mdlg">
                  {(subdomainSuccessful || Boolean(privacyCenterDomain)) && (
                    <Callout variant={CalloutVariant.Green} className="info-callout">
                      <div className="info-callout--header">
                        <CheckCircle />
                        <span>Great! Your subdomain is verified.</span>
                      </div>
                    </Callout>
                  )}
                  {subdomainError === true && (
                    <Callout variant={CalloutVariant.Red} className="info-callout">
                      <div className="info-callout--header">
                        <Info />
                        <span>Subdomain could not be verified</span>
                      </div>
                      <p>
                        Oops! Looks like your subdomain is not set up. Please follow the
                        instructions to set it up and retry verification.
                      </p>
                    </Callout>
                  )}
                  {subdomainError && subdomainError !== true && (
                    <Callout variant={CalloutVariant.Red} className="info-callout">
                      <div className="info-callout--header">
                        <Info />
                        <span>Subdomain could not be verified</span>
                      </div>
                      <p>
                        Oops! Looks like {subdomainError} points to the wrong CNAME. Copy and paste
                        the value at item 7 above as the CNAME Destination and retry verification.
                      </p>
                    </Callout>
                  )}
                  {subdomainError != "" && (
                    <>
                      <ol className="instruction-list p-0">
                        <li>
                          <div className="text-body-1-medium m-0">
                            Follow the instructions below to set up your custom domain for your
                            privacy center.
                          </div>
                          <ol className="instruction-list">
                            <li>
                              Login to your domain host (for example: Google Domains, GoDaddy, Hover
                              and similar).
                            </li>
                            <li>Click on your company’s domain.</li>
                            <li>
                              Find the menu which says <strong>DNS</strong> or{" "}
                              <strong>DNS Records</strong> and click on it.
                            </li>
                            <li>
                              Click <strong>Add New Record</strong> (it might say “Create new
                              record” or something similar).
                            </li>
                            <li>
                              Select <strong>CNAME</strong> as the <strong>Type</strong>.
                            </li>
                            <li className="d-flex flex-row">
                              <span>
                                At <strong>CNAME Host Name</strong>:{" "}
                              </span>
                              <CodeSnippet inline={true}>privacy</CodeSnippet>
                            </li>
                            <li>
                              <p>
                                Copy and paste the value below under <strong>Value</strong> (it
                                might also be called <strong>Data</strong> or{" "}
                                <strong>CNAME Destination</strong>).
                              </p>
                              <div className="d-flex flex-row">
                                <span>
                                  <strong>CNAME Value</strong>:{" "}
                                </span>
                                <CodeSnippet inline={true}>
                                  {getPrivacyCenterCnameValue(privacyCenter)}
                                </CodeSnippet>
                              </div>
                            </li>
                            <li>
                              Click <strong>Save</strong>.
                            </li>
                          </ol>
                        </li>

                        <li>
                          <p>Have a developer? Share this with them:</p>
                          <ol className="instruction-list">
                            <li className="d-flex flex-row">
                              <span>
                                <strong>CNAME Host Name</strong>:{" "}
                              </span>
                              <CodeSnippet inline={true}>privacy</CodeSnippet>
                            </li>
                            <li className="d-flex flex-row">
                              <span>
                                <strong>CNAME Value</strong>:{" "}
                              </span>
                              <CodeSnippet inline={true}>
                                {getPrivacyCenterCnameValue(privacyCenter)}
                              </CodeSnippet>
                            </li>
                          </ol>
                        </li>
                      </ol>

                      <div className="mt-mdlg">
                        <Callout variant={CalloutVariant.LightPurple} className="info-callout">
                          <div className="info-callout--header">
                            <ContactSupportIcon className="mr-sm" />
                            <b>Need extra help?</b>
                            <a
                              href="#"
                              className="regular-link ml-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                (window as any).Beacon("open");
                              }}
                            >
                              Contact our customer support
                            </a>
                          </div>
                        </Callout>
                      </div>
                    </>
                  )}
                </div>
              </form>
            );
          }}
        />
      </div>
    </GetCompliantPageWrapper>
  );
};
